import { useSyncExternalStore } from 'react';
import { brandOf, isAbsolute, layoutOf, readInput, TELEPHONY, U, writeOutput, type CollectionInfo, type Layout, type Usage } from './hid';
import { HeadsetLogic, IDLE, lightsFor, sameLights, type Press, type Situation } from './logic';

/**
 * A headset's buttons and lights in Stitch (1.13.0, Teampace 3141; Joey, Nov 2: "Jabra, Yealink and
 * Plantronics integration for headsets into all of our call control surfaces"). Through WebHID:
 * Chrome and Edge, and the Mac and Windows apps (their shell lets the page reach a telephony headset,
 * Stitch Desktop 3142). Safari and Firefox can't reach one at all.
 *
 * - Connecting asks the browser once (it must come from a click); after that the headset is found
 *   again by itself, here and after a restart, and when it's plugged back in.
 * - One Stitch tab runs the headset at a time (a Web Lock), so two tabs never both answer.
 * - What's on comes from the call, Patch and meeting surfaces (each registers: what rings, what's
 *   on, and what to do), in that order: a call in the app first, then a Patch, then a meeting.
 * - Nothing about the headset leaves this browser: the choice is kept here only.
 */

// --- the parts of WebHID used (not in TypeScript's DOM types) --------------------------------------

interface HidDevice extends EventTarget {
  opened: boolean;
  vendorId: number;
  productId: number;
  productName: string;
  collections: CollectionInfo[];
  open(): Promise<void>;
  close(): Promise<void>;
  sendReport(reportId: number, data: Uint8Array): Promise<void>;
}
interface HidInputEvent extends Event {
  device: HidDevice;
  reportId: number;
  data: DataView;
}
interface Hid extends EventTarget {
  getDevices(): Promise<HidDevice[]>;
  requestDevice(o: { filters: { usagePage?: number; usage?: number; vendorId?: number }[] }): Promise<HidDevice[]>;
}
const hid = (): Hid | null => (typeof navigator !== 'undefined' && 'hid' in navigator ? ((navigator as unknown as { hid: Hid }).hid ?? null) : null);

/** This browser can reach a headset (Chrome, Edge, the desktop apps). */
export const headsetSupported = (): boolean => hid() !== null && typeof window !== 'undefined' && window.isSecureContext !== false;

// --- what the surfaces say -------------------------------------------------------------------------

/** One place calls happen (a call in the app, a Patch, a meeting): what rings, what's on, what to do. */
export interface HeadsetSurface {
  /** Lower comes first. */
  order: number;
  ringing(): boolean;
  active(): boolean;
  muted(): boolean;
  held?(): boolean;
  answer?(): unknown;
  decline?(): unknown;
  hangup(): unknown;
  toggleMute(): unknown;
  hold?(): unknown;
  /** Calls `changed` whenever any of the above may have changed; returns a stop. */
  watch(changed: () => void): () => void;
}

const surfaces = new Map<string, HeadsetSurface>();
const unwatch = new Map<string, () => void>();

/** A surface takes part (once each, by name). */
export function registerHeadsetSurface(name: string, s: HeadsetSurface): void {
  unwatch.get(name)?.();
  surfaces.set(name, s);
  unwatch.set(name, s.watch(() => refreshLights()));
  refreshLights();
}

/** The surface a press goes to: one that's on, else one that rings. */
function current(): { ringing: HeadsetSurface | null; active: HeadsetSurface | null } {
  const list = [...surfaces.values()].sort((a, b) => a.order - b.order);
  return { active: list.find((s) => s.active()) ?? null, ringing: list.find((s) => s.ringing()) ?? null };
}

export function situationNow(): Situation {
  const { active, ringing } = current();
  if (!active && !ringing) return IDLE;
  return {
    ringing: Boolean(ringing),
    active: Boolean(active),
    muted: active ? active.muted() : false,
    held: active?.held?.() ?? false,
    canHold: Boolean(active?.hold)
  };
}

// --- the headset ------------------------------------------------------------------------------------

export interface HeadsetState {
  supported: boolean;
  /** The headset Stitch uses, when one is connected. */
  device: { name: string; brand: string | null; vendorId: number; productId: number } | null;
  /** This tab runs it (another Stitch tab may, when not). */
  leading: boolean;
  /** Buttons on (the person can turn them off; the lights go off too). */
  enabled: boolean;
  /** The last button press, in words, for the test in Your settings. */
  lastPress: string | null;
  problem: string | null;
}

const KEY_ON = 'stitch:headset-on';
const KEY_DEVICE = 'stitch:headset-device';
const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string | null) => {
  try {
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    /* kept for this visit only */
  }
};

let state: HeadsetState = { supported: false, device: null, leading: false, enabled: read(KEY_ON) !== 'off', lastPress: null, problem: null };
const listeners = new Set<() => void>();
function set(next: Partial<HeadsetState>): void {
  state = { ...state, ...next };
  for (const l of [...listeners]) l();
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => void listeners.delete(l);
};
export const useHeadset = (): HeadsetState => useSyncExternalStore(subscribe, () => state, () => state);
export const headsetNow = (): HeadsetState => state;

let device: HidDevice | null = null;
let layout: Layout | null = null;
let logic = new HeadsetLogic();
let shown: Map<Usage, number> | null = null;
/** A test in Your settings: lights on for a moment, whatever is happening. */
let testing: Map<Usage, number> | null = null;

const WORDS: Record<Press, string> = { answer: 'Answer', decline: 'Decline', hangup: 'Hang up', mute: 'Mute', hold: 'Hold' };

function onInput(e: Event): void {
  const ev = e as HidInputEvent;
  if (!layout || ev.device !== device) return;
  const values = readInput(layout, ev.reportId, ev.data);
  if (!values.size) return;
  const s = state.enabled && state.leading ? situationNow() : IDLE;
  const press = logic.press(values, s);
  // The test in Your settings: any button, named.
  const named = [...values].find(([, v]) => v !== 0)?.[0];
  if (named !== undefined) set({ lastPress: press ? WORDS[press] : buttonName(named) });
  if (!press || !state.enabled || !state.leading) return;
  const { active, ringing } = current();
  void Promise.resolve()
    .then(() => {
      if (press === 'answer') return ringing?.answer?.();
      if (press === 'decline') return ringing?.decline?.();
      if (press === 'hangup') return active?.hangup();
      if (press === 'mute') return active?.toggleMute();
      if (press === 'hold') return active?.hold?.();
      return undefined;
    })
    .catch(() => undefined);
}

function buttonName(u: Usage): string {
  if (u === U.hookSwitch) return 'Call button';
  if (u === U.phoneMute) return 'Mute';
  if (u === U.flash) return 'Hold (flash)';
  if (u === U.drop || u === U.lineBusyTone) return 'Reject';
  if (u === U.redial) return 'Redial';
  return 'A button';
}

/** The lights for what's happening now, sent when they change. */
function refreshLights(): void {
  if (!device?.opened || !layout) return;
  // Another Stitch tab runs the headset: its lights are that tab's (only the test here lights them).
  if (!state.leading && !testing) return;
  const s = state.enabled && state.leading ? situationNow() : IDLE;
  const want = testing ?? lightsFor(s);
  if (sameLights(shown, want)) return;
  shown = want;
  if (!testing) logic.synced(s.active);
  for (const r of writeOutput(layout, want)) device.sendReport(r.id, r.data).catch(() => undefined);
}

async function use(d: HidDevice): Promise<void> {
  if (device === d && d.opened) return;
  if (device && device !== d) await device.close().catch(() => undefined);
  const l = layoutOf(d.collections);
  if (!l.telephony || !l.inputs.size) {
    set({ problem: `${d.productName || 'That device'} doesn’t have call buttons Stitch can use.` });
    return;
  }
  try {
    if (!d.opened) await d.open();
  } catch {
    set({ problem: `${d.productName || 'The headset'} is busy in another app. Close it there, or plug the headset in again.` });
    return;
  }
  device = d;
  layout = l;
  logic = new HeadsetLogic(isAbsolute(l, U.hookSwitch));
  shown = null;
  d.addEventListener('inputreport', onInput);
  write(KEY_DEVICE, `${d.vendorId}:${d.productId}`);
  set({ device: { name: d.productName || 'Headset', brand: brandOf(d.vendorId), vendorId: d.vendorId, productId: d.productId }, problem: null });
  refreshLights();
}

function forget(d: HidDevice): void {
  if (d !== device) return;
  d.removeEventListener('inputreport', onInput);
  device = null;
  layout = null;
  shown = null;
  set({ device: null });
}

const telephonyOnly = (list: HidDevice[]) => list.filter((d) => d.collections.some((c) => c.usagePage === TELEPHONY));

/** The headset this browser was allowed before (the one chosen last, else the first). */
async function reconnect(): Promise<void> {
  const h = hid();
  if (!h) return;
  const list = telephonyOnly(await h.getDevices().catch(() => []));
  if (!list.length) return;
  const saved = read(KEY_DEVICE);
  const pick = list.find((d) => `${d.vendorId}:${d.productId}` === saved) ?? list[0]!;
  await use(pick);
}

/** "Connect a headset": the browser lists telephony headsets, the person picks one. From a click only. */
export async function connectHeadset(): Promise<boolean> {
  const h = hid();
  if (!h) return false;
  try {
    const [d] = await h.requestDevice({ filters: [{ usagePage: TELEPHONY }] });
    if (!d) return false;
    await use(d);
    return Boolean(device);
  } catch {
    set({ problem: 'The headset wasn’t connected. Try again, and choose it in the list.' });
    return false;
  }
}

/** Stops using the headset here (the browser keeps its permission; Connect finds it again). */
export async function disconnectHeadset(): Promise<void> {
  const d = device;
  if (!d) return;
  shown = null;
  if (layout) for (const r of writeOutput(layout, lightsFor(IDLE))) await d.sendReport(r.id, r.data).catch(() => undefined);
  forget(d);
  write(KEY_DEVICE, null);
  await d.close().catch(() => undefined);
}

/** Buttons and lights on or off (on this browser). */
export function setHeadsetEnabled(on: boolean): void {
  write(KEY_ON, on ? null : 'off');
  set({ enabled: on });
  refreshLights();
}

/** The test in Your settings: the ring and mute lights for three seconds, then back to what's happening. */
export function testHeadset(): void {
  if (!device || !layout) return;
  testing = new Map([
    [U.ledRing, 1],
    [U.ledMute, 1],
    [U.ledOffHook, 1]
  ]);
  set({ lastPress: null });
  refreshLights();
  window.setTimeout(() => {
    testing = null;
    shown = null;
    refreshLights();
  }, 3000);
}

let started = false;
/** Once, when Stitch starts: find the headset again, take the lead when no other tab has it. */
export function startHeadset(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  const h = hid();
  set({ supported: headsetSupported() });
  if (!h) return;
  h.addEventListener('connect', (e) => {
    const d = (e as unknown as { device: HidDevice }).device;
    if (!device && d.collections.some((c) => c.usagePage === TELEPHONY) && `${d.vendorId}:${d.productId}` === read(KEY_DEVICE)) void use(d);
  });
  h.addEventListener('disconnect', (e) => forget((e as unknown as { device: HidDevice }).device));
  const locks = (navigator as unknown as { locks?: { request(name: string, cb: () => Promise<void>): Promise<void> } }).locks;
  const lead = () => {
    set({ leading: true });
    refreshLights();
  };
  if (locks?.request) {
    // Held until this tab closes: the next tab in line takes over then.
    void locks.request('stitch-headset', () => new Promise<void>(() => lead())).catch(() => lead());
  } else lead();
  void reconnect();
}
